
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __loadNamespaces from 'next-translate/loadNamespaces'
// @ts-ignore
    
import { kebabCase } from 'lodash'
import { GetServerSideProps, NextPage } from 'next'

import { CateCity, CitiesProvider } from '@/context/Cities'
import ListingsMapHead from '@/heads/listings-map'
import { isValidSegmentForHome } from '@/i18n-routes'
import { getCitiesByArea } from '@/page-commons/citiesQuery'
import { ListingsByCityPage } from '@/page-commons/ListingsByCityPage'
import { AreaDescription } from '@/types/graphql-generated-types'
import { toSession, UserWithKratosId } from '@/utils/auth/auth-client-ssr'
import citiesMapper, { areaParamDecoder, isAreaParam } from '@/utils/cities-mapper'
import { PageType } from '@/utils/get-page-type-from-params'
import listingFacilities from '@/utils/listing-facilities'
import { isLocaleEnabledFor } from '@/utils/locale-variables'

type Props = {
  areas: AreaDescription[]
  cities: CateCity[]
  city?: string
  geoSlugs?: string[]
  pageType: PageType // hardcoded to 'city' in getServerSideProps
  user: UserWithKratosId | null
}

/**
 * This page shows content for
 *  - /<lang>/<houses-for-sale>/<city>
 *  - /<lang>/<houses-for-sale>/<city>-provincia
 *
 * @param areas array with a single element, AreaDescription for the primary city in area (i.e. /milano, /rho, /milano-provincia will have the same value)
 * @param cities all the cities in the area where the current city belongs (i.e. /milano, /rho, /milano-provincia will have the same list)
 * @param city the name of the city in kebab case, or null if <CITY>-provincia
 * @param geoSlugs the single geo-slug for the city when a single city, or the list of geo-slugs for all cities in the area in any other case
 * @param user the current user | null
 * @constructor
 */
const HomeCityPage: NextPage<Props> = ({ areas, cities, city, geoSlugs, user }) => {
  return (
    <>
      <CitiesProvider areas={areas} cities={cities}>
        <>
          <ListingsMapHead />
          <ListingsByCityPage canonicalFilter={undefined} city={city} geoSlugs={geoSlugs} user={user} />
        </>
      </CitiesProvider>
    </>
  )
}

/**
 * Verify is the current value of [city] is a valid segment for "area".
 *
 * @example
 * isValidForAreaPage("milano-provincia", primaryCity) // true
 * isValidForAreaPage("rho-provincia", primaryCity) // false
 *
 * @param dynamicSegment the value for [city] page param
 * @param primaryCity a CateCity for the primary city in the area (for instance, Milano)
 */
function isValidForAreaPage(dynamicSegment: string, primaryCity: CateCity) {
  const cityKebab = areaParamDecoder(dynamicSegment)
  const primaryCityKebab = primaryCity.slug.split('/')[1]

  return primaryCityKebab === cityKebab
}

 const _getServerSideProps: GetServerSideProps = async (ctx) => {
  const currentAreaParam = ctx.params.city.toString()
  const homeSegment = ctx.params.home.toString()
  const locale = ctx.locale as Locale

  // == roma if roma, roma if roma-provincia
  const cityWithOutAreaSuffix = areaParamDecoder(currentAreaParam)

  if (!isValidSegmentForHome(locale, homeSegment) || !listingFacilities.validateCity(cityWithOutAreaSuffix, locale)) {
    return {
      notFound: true,
    }
  }

  const allCitiesInArea: CateCity[] = await getCitiesByArea(ctx.locale, cityWithOutAreaSuffix)
  const primaryCity = allCitiesInArea.filter((city) => city.referenceArea === city.slug)

  if (
    isLocaleEnabledFor('searchByArea', locale) &&
    isAreaParam(currentAreaParam) &&
    !isValidForAreaPage(currentAreaParam, primaryCity[0])
  ) {
    return {
      notFound: true,
    }
  }

  // null when /milano-provincia
  const currentCity: CateCity = citiesMapper.findCity(currentAreaParam, allCitiesInArea)

  const citiesOrCurrent = currentCity ? [currentCity] : allCitiesInArea

  const city = currentCity ? encodeCityParam(currentCity, ctx.locale) : null
  const geoSlugs = citiesOrCurrent.map((city) => city.slug)

  const { ory_kratos_session } = ctx.req.cookies
  const user = await toSession(ory_kratos_session)

  return {
    props: {
      areas: primaryCity,
      cities: allCitiesInArea,
      city,
      geoSlugs,
      pageType: 'city',
      user
    },
  }
}

function encodeCityParam(city: CateCity, locale: string) {
  const loc = locale === 'en' ? 'it' : locale
  const cityName = city.translations.find((t) => t.language === loc).value
  return kebabCase(cityName)
}

export default HomeCityPage


// @ts-ignore
    export async function getServerSideProps(ctx) {
// @ts-ignore
        let res = _getServerSideProps(ctx)
// @ts-ignore
        if(typeof res.then === 'function') res = await res
// @ts-ignore
        return {
// @ts-ignore
          
// @ts-ignore
          ...res,
// @ts-ignore
          props: {
// @ts-ignore
            ...(res.props || {}),
// @ts-ignore
            ...(await __loadNamespaces({
// @ts-ignore
              ...ctx,
// @ts-ignore
              pathname: '/[home]/[city]/index',
// @ts-ignore
              loaderName: 'getServerSideProps',
// @ts-ignore
              ...__i18nConfig,
// @ts-ignore
              
// @ts-ignore
            }))
// @ts-ignore
          }
// @ts-ignore
        }
// @ts-ignore
    }
// @ts-ignore
  